import React, { useCallback, forwardRef } from "react";
import { colors2, icons, typoV2_CSS } from "oolib";
import styled from "styled-components";
import { Lightbox, LightboxTrigger, LightboxContent, useLightbox } from "@/components/UI/lightbox";
import { StyledListWrapper } from "@/components/UI/List";
import { ListItem } from "@/components/UI/List/comps/ListItem";

const { CaretDown } = icons;

// ─── Button ────────────────────────────────────────────────────────
// Compact bordered button. Spreads native button props + forwards ref (so it
// works as a <LightboxTrigger asChild> target). Square it via style for an
// icon-only button; default suits a label trigger.

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 0 6px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  ${typoV2_CSS.UI_CAPTION_SEMIBOLD}
  color: ${colors2.grey80};
  transition: background 0.15s ease;

  &:hover {
    background: ${colors2.grey5};
  }
`;

export const Button = forwardRef((props, ref) => (
  <StyledButton ref={ref} type="button" {...props} />
));

// ─── Input ─────────────────────────────────────────────────────────
// A single, borderless input cell. Spreads native input props. Designed to
// fill a sized flex parent such as <InputGroup/>.

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 1px 5px;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  ${typoV2_CSS.UI_CAPTION_SEMIBOLD}
  color: ${colors2.grey60};

  &::placeholder {
    color: ${colors2.grey50};
  }

  /* hide native number spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const Input = forwardRef((props, ref) => <StyledInput ref={ref} {...props} />);

// ─── InputGroup ────────────────────────────────────────────────────
// Bordered, rounded container that merges its children into one field,
// inserting a divider between each. Drop any number of <Input/>s inside.

export const InputGroup = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 6px;
  overflow: hidden;

  & > * + * {
    border-left: 1px solid ${colors2.grey10};
  }
`;

// ─── Dropdown ──────────────────────────────────────────────────────
// Complete compact single-select: renders its own pill trigger (selected
// label + caret) and a Lightbox + List menu. Works for any options.

const DropdownList = ({ options, value, onChange }) => {
  const lightbox = useLightbox();
  const noop = useCallback(() => {}, []);

  const handleChange = useCallback(
    (option) => {
      onChange(option.value);
      lightbox?.setOpen(false);
    },
    [onChange, lightbox]
  );

  return (
    <StyledListWrapper disableShadow>
      {options.map((option, index) => (
        <ListItem
          key={option.value}
          option={option}
          index={index}
          S
          isSelected={option.value === value}
          isFocussed={false}
          scrollFocussedOpIntoView={noop}
          onChange={handleChange}
        />
      ))}
    </StyledListWrapper>
  );
};

export const Dropdown = ({
  options = [],
  value,
  onChange,
  placement = "bottom-start",
  contentStyle,
  disabled = false,
}) => {
  const current = options.find((o) => o.value === value);

  return (
    <Lightbox placement={placement}>
      <LightboxTrigger asChild>
        {/* A native disabled button swallows the click, so the menu can't open */}
        <Button style={{ flexShrink: 0 }} disabled={disabled}>
          <span>{current?.display}</span>
          <CaretDown size={12} color={colors2.grey80} />
        </Button>
      </LightboxTrigger>
      <LightboxContent style={{ zIndex: 10001, ...contentStyle }}>
        <DropdownList options={options} value={value} onChange={onChange} />
      </LightboxContent>
    </Lightbox>
  );
};
